import { isValid, parseISO } from 'date-fns';

/**
 * Date utility functions for handling date-only strings (YYYY-MM-DD format)
 * to avoid timezone issues when converting between strings and Date objects.
 *
 * Uses local timezone for date handling and leverages date-fns for ISO string parsing.
 */

/**
 * Parses a date-only string (YYYY-MM-DD), timestamp, or Date object and returns a Date object
 * representing that date at midnight in local timezone.
 *
 * @param dateString - Date string in YYYY-MM-DD format, timestamp in milliseconds, or Date object
 * @returns Date object at local midnight, or null if invalid
 *
 * @example
 * parseDateOnly('2025-12-08') // => Dec 8, 2025 00:00:00 (local timezone)
 * parseDateOnly(1702080000000) // => Dec 9, 2023 00:00:00 (local timezone)
 * parseDateOnly(new Date()) // => today at 00:00:00 (local timezone)
 */
export function parseDateOnly(dateString: string | number | Date | null | undefined): Date | null {
  if (!dateString) return null;

  // Handle Date object
  if (dateString instanceof Date) {
    if (!isValid(dateString)) return null;
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  // Handle timestamp (number)
  if (typeof dateString === 'number') {
    const date = new Date(dateString);
    if (!isValid(date)) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  }

  // Handle string format using date-fns parseISO
  // parseISO parses ISO 8601 strings in local timezone, avoiding UTC interpretation issues
  const date = parseISO(dateString);
  return isValid(date) ? date : null;
}
